import React, { Dispatch, FC, SetStateAction, useEffect, useState, useMemo } from 'react';

import Spotlights from 'components/Spotlights';
import LoaderContent from 'components/UI/LoaderContent';
import useTheme from 'helpers/useTheme';
import getStyles from 'helpers/getStyles';

import { CardTypeOptions, ECollectionLayoutOptions, UserObjectType } from 'constants/graphqlTypes';
import { useGetSpotlights } from 'graphQL/spotlights/hook';
import { COLORS } from 'styles/constants';
import { useNavigationRef } from 'components/Navigation/helpers/hooks';
import { useProfileContext } from 'helpers/useProfileProvider';

import ListGrid from 'components/List/Grid';
import usePageType from 'helpers/usePageType';
import { useGetUserCards, useGetFilteredUserPicks, useGetUserPickedTopics } from 'graphQL/cards/userCards/hooks';
import Info from './Info';
import { StyledSection } from './styled';
import ProfileContent from '../ProfileContent';
import PicksFilter from '../PicksFilter/PicksFilter';

interface IProfileTopProps {
  userData?: UserObjectType;
  loading: boolean;
  setHideSpotlights: Dispatch<SetStateAction<boolean>>;
  hideSpotlights: boolean;
  onGradientChange?: (gradientStart: string | null, gradientEnd: string | null) => void;
}

const ProfileTop: FC<IProfileTopProps> = ({
  userData,
  loading,
  setHideSpotlights,
  hideSpotlights,
  onGradientChange,
}) => {
  const [index, setIndex] = useState(0);
  const [hiddenCards, setHiddenCards] = useState<string[]>([]);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [selectedFilters, setSelectedFilters] = useState<{
    topicIds?: number[];
    mediaTagNames?: string[];
  }>({});

  const spotlightsData = useGetSpotlights();
  const isDark = useTheme();
  const { isMyProfile } = usePageType();
  const { cards, loading: spotlightsLoading, listsCount } = spotlightsData;
  const { gradientStart, gradientEnd } = getStyles(cards[index]?.cardStyle);
  const [hideGradient, setHideGradient] = useState(false);
  const { setGradientStart, setGradientEnd } = useProfileContext();
  const { userInfo, userStyle, id: userId, username } = userData ?? {};
  const type = userStyle?.layoutCollection === ECollectionLayoutOptions.ImageGrid ? CardTypeOptions.Card : undefined;
  const { recentCards, loadMore, totalCount, loading: loadingItems } = useGetUserCards({
    type,
    includeQuestionResponse: true,
    username,
  });

  // Get media tags to convert names to IDs
  const { mediaTags } = useGetUserPickedTopics();

  // Convert media tag names to IDs for the GraphQL query
  const mediaTagIds = useMemo(() => {
    if (!selectedFilters.mediaTagNames?.length || !mediaTags?.length) return undefined;

    // Convert string IDs to numbers if needed
    const mediaTagNameToId = new Map(
      mediaTags.map((tag: { id: string | number; name: string }) => [
        tag.name,
        typeof tag.id === 'string' ? parseInt(tag.id, 10) : tag.id,
      ])
    );
    const ids = selectedFilters.mediaTagNames
      .map((name) => mediaTagNameToId.get(name))
      .filter((id): id is number => id !== undefined && !Number.isNaN(id));

    return ids;
  }, [selectedFilters.mediaTagNames, mediaTags]);

  // Use filtered picks when filters are applied, otherwise use regular picks
  const {
    filteredPicks,
    loadMore: loadMoreFiltered,
    totalCount: filteredTotalCount,
    loading: loadingFilteredItems,
  } = useGetFilteredUserPicks({
    topicIds: selectedFilters.topicIds,
    mediaTagIds,
  });

  const hasActiveFilters = selectedFilters.topicIds?.length || selectedFilters.mediaTagNames?.length;

  // Use the appropriate data source based on whether filters are applied
  const displayCards = hasActiveFilters ? filteredPicks : recentCards;
  const displayLoadMore = hasActiveFilters ? loadMoreFiltered : loadMore;
  const displayTotalCount = hasActiveFilters ? filteredTotalCount : totalCount;
  const displayLoading = hasActiveFilters ? loadingFilteredItems : loadingItems;
  const { avatarColorGradientStart, avatarColorGradientEnd } = userInfo ?? {};

  const isCurrentSpotlightHidden = hiddenCards.includes(cards[index]?.id);

  const bothReady = totalCount !== 0 && listsCount !== 0;

  useEffect(() => {
    if (gradientEnd && gradientStart) {
      setGradientEnd?.(gradientEnd);
      setGradientStart?.(gradientStart);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradientStart, gradientEnd]);

  // Call the parent callback when gradient changes
  useEffect(() => {
    const currentGradientStart = activeTabIndex === 0 ? gradientStart ?? avatarColorGradientStart ?? null : null;
    const currentGradientEnd = activeTabIndex === 0 ? gradientEnd ?? avatarColorGradientEnd ?? null : null;
    onGradientChange?.(currentGradientStart, currentGradientEnd);
  }, [gradientStart, gradientEnd, avatarColorGradientStart, avatarColorGradientEnd, activeTabIndex, onGradientChange]);

  const fontColor = isDark || !hideGradient ? COLORS.white[100] : COLORS.brown.dark[100];
  const cardIds = cards.map(({ id }) => id);
  const spotlightsChangeKey = JSON.stringify(cardIds);
  const ref = useNavigationRef(fontColor);

  useEffect(() => {
    setIndex(0);
  }, [spotlightsChangeKey]);

  const hiddenCardIds = useMemo(() => cards.filter(({ isHidden }) => !!isHidden).map(({ id }) => id), [cards]);

  useEffect(() => {
    setHiddenCards(hiddenCardIds);
  }, [hiddenCardIds]);

  useEffect(() => {
    setHideSpotlights(spotlightsLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spotlightsLoading]);

  useEffect(() => {
    setHideGradient(!cards.length || isCurrentSpotlightHidden || spotlightsLoading || loading);
  }, [isCurrentSpotlightHidden, cards.length, spotlightsLoading, loading]);

  useEffect(() => {
    setActiveTabIndex(0);
  }, [userId]);

  const handleTabChange = (tabIndex: number) => {
    setActiveTabIndex(tabIndex);
  };

  const handleFiltersChange = (filters: { topicIds?: number[]; mediaTagNames?: string[] }) => {
    setSelectedFilters(filters);
  };

  const renderContent = () => {
    if (loading) {
      return <LoaderContent isCentered isDark={isDark} isFullScreen />;
    }

    return (
      <>
        <Info
          isDark={isDark}
          showGradient={activeTabIndex === 1}
          userData={userData}
          fontColor={fontColor}
          noContent={!cards.length}
          hideGradient={hideGradient}
          onTabChange={handleTabChange}
          cardsCount={bothReady ? displayTotalCount : 0}
          listsCount={bothReady ? listsCount : 0}
          gradientStart={activeTabIndex === 1 ? avatarColorGradientStart : null}
          gradientEnd={activeTabIndex === 1 ? avatarColorGradientEnd : null}
          showStyledGrain={false}
        />

        {activeTabIndex === 0 && !hideSpotlights && (
          <Spotlights
            hiddenCards={hiddenCards}
            setHiddenCards={setHiddenCards}
            spotlightsData={spotlightsData}
            fontColor={fontColor}
            setIndex={setIndex}
            key={spotlightsChangeKey}
          />
        )}

        {activeTabIndex === 0 && userData && (
          <>
            <PicksFilter onFiltersChange={handleFiltersChange} />
            <ListGrid
              hideActionsInfo={isMyProfile}
              layout={ECollectionLayoutOptions.Default}
              items={displayCards}
              loadMore={displayLoadMore}
              loading={displayLoading}
              isDarkFromProps={isDark}
              isProfilePicks
            />
          </>
        )}

        {activeTabIndex === 1 && userData && <ProfileContent />}
      </>
    );
  };

  return (
    <StyledSection ref={cards.length ? ref : null} fontColor={fontColor} fixedHeight={!!cards.length} isDark={isDark}>
      {renderContent()}
    </StyledSection>
  );
};

export default ProfileTop;
